import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Clock, Plus, LogIn, LogOut, User } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useLiveMode } from '../context/LiveModeContext';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const OutOfClassPage = () => {
  const { classId } = useParams();
  const { session, refreshStats } = useLiveMode();
  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({ student_id: '', reason: '', notes: '' });

  const fetchData = async () => {
    try {
      const sessionParam = session?.session_id ? `?session_id=${session.session_id}` : '';
      const [recordsRes, studentsRes, classesRes] = await Promise.all([
        fetch(`${API}/out-of-class/${classId}${sessionParam}`, { credentials: 'include' }),
        fetch(`${API}/students/${classId}`, { credentials: 'include' }),
        fetch(`${API}/classes`, { credentials: 'include' })
      ]);
      if (recordsRes.ok) setRecords(await recordsRes.json());
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (classesRes.ok) {
        const classes = await classesRes.json();
        setClassInfo(classes.find(c => c.class_id === classId));
      }
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [classId]);

  const handleSignOut = async () => {
    if (!newRecord.student_id || !newRecord.reason.trim()) { toast.error('Student and reason are required'); return; }
    try {
      const r = await fetch(`${API}/out-of-class`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newRecord, class_id: classId, period: session?.class_id === classId ? session.period : null, session_id: session?.session_id || null })
      });
      if (r.ok) { toast.success('Student signed out'); setDialogOpen(false); setNewRecord({ student_id: '', reason: '', notes: '' }); fetchData(); refreshStats(); }
    } catch (error) { toast.error('Failed to sign out'); }
  };

  const handleReturn = async (recordId) => {
    try {
      const r = await fetch(`${API}/out-of-class/return/${recordId}`, { method: 'PUT', credentials: 'include' });
      if (r.ok) { toast.success('Student returned'); fetchData(); refreshStats(); }
    } catch (error) { toast.error('Failed to mark return'); }
  };

  const getStudentName = (studentId) => students.find(s => s.student_id === studentId)?.name || 'Unknown';

  const activeRecords = records.filter(r => !r.time_in);
  const returnedRecords = records.filter(r => r.time_in);

  const commonReasons = ['Bathroom', 'Drink', 'Office', 'Nurse', 'Counselor', 'Other'];

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Out of Class' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="space-y-6" data-testid="out-of-class-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-medium text-google-grey-900">Out of Class</h1>
            <p className="text-google-grey-600 mt-1">{classInfo?.name} &middot; {activeRecords.length} currently out</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-google-blue hover:bg-blue-600 text-white shadow-google hover:shadow-google-hover transition-shadow" data-testid="sign-out-btn">
                <LogOut className="w-4 h-4 mr-2" />Sign Out Student
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-heading text-xl text-google-grey-900">Sign Out Student</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="text-sm font-medium text-google-grey-700">Student *</label>
                  <Select value={newRecord.student_id} onValueChange={(v) => setNewRecord({ ...newRecord, student_id: v })}>
                    <SelectTrigger className="mt-1 border-google-grey-300" data-testid="student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => <SelectItem key={s.student_id} value={s.student_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-google-grey-700">Reason *</label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-2">
                    {commonReasons.map(reason => (
                      <Button key={reason} type="button" variant={newRecord.reason === reason ? 'default' : 'outline'} size="sm"
                        className={newRecord.reason === reason ? 'bg-google-blue text-white' : 'border-google-grey-300 text-google-grey-600'}
                        onClick={() => setNewRecord({ ...newRecord, reason })}>
                        {reason}
                      </Button>
                    ))}
                  </div>
                  <Input placeholder="Or enter custom reason" value={!commonReasons.includes(newRecord.reason) ? newRecord.reason : ''} onChange={(e) => setNewRecord({ ...newRecord, reason: e.target.value })} className="border-google-grey-300" data-testid="reason-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-google-grey-700">Notes (optional)</label>
                  <Input placeholder="Any additional notes" value={newRecord.notes} onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })} className="mt-1 border-google-grey-300" />
                </div>
                <Button onClick={handleSignOut} className="w-full bg-google-blue hover:bg-blue-600 text-white" data-testid="confirm-sign-out-btn">Sign Out</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Active out of class */}
        <div>
          <h2 className="text-lg font-medium text-google-grey-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-google-red" />Currently Out ({activeRecords.length})
          </h2>
          {loading ? (
            <div className="text-center py-8 text-google-grey-500">Loading...</div>
          ) : activeRecords.length === 0 ? (
            <Card className="bg-white border border-dashed border-google-grey-300 shadow-none">
              <CardContent className="py-8 text-center">
                <p className="text-google-grey-500">No students currently out of class</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeRecords.map((record) => (
                <Card key={record.record_id} className="bg-white border border-google-grey-200 border-l-4 border-l-google-red" data-testid={`active-record-${record.record_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center"><User className="w-4 h-4 text-google-red" /></div>
                        <span className="font-medium text-google-grey-900">{getStudentName(record.student_id)}</span>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-google-grey-600 mb-3">
                      <p><span className="font-medium">Reason:</span> {record.reason}</p>
                      <p><span className="font-medium">Time out:</span> {record.time_out && formatDistanceToNow(new Date(record.time_out), { addSuffix: true })}</p>
                      {record.notes && <p><span className="font-medium">Notes:</span> {record.notes}</p>}
                    </div>
                    <Button onClick={() => handleReturn(record.record_id)} className="w-full bg-google-green hover:bg-green-600 text-white" data-testid={`return-btn-${record.record_id}`}>
                      <LogIn className="w-4 h-4 mr-2" />Mark Returned
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {returnedRecords.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-google-grey-900 mb-4">History</h2>
            <Card className="bg-white border border-google-grey-200">
              <CardContent className="p-0 divide-y divide-google-grey-200">
                {returnedRecords.slice(0, 10).map((record) => (
                  <div key={record.record_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-google-grey-100 flex items-center justify-center"><User className="w-4 h-4 text-google-grey-500" /></div>
                      <div>
                        <p className="font-medium text-google-grey-900">{getStudentName(record.student_id)}</p>
                        <p className="text-sm text-google-grey-500">{record.reason}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-google-grey-500">
                      <p>Out: {record.time_out && format(new Date(record.time_out), 'h:mm a')}</p>
                      <p>In: {record.time_in && format(new Date(record.time_in), 'h:mm a')}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};
